import { IBillingRestClient } from "./i-billing-rest.client";
import { IPriceList } from "../../models/i-pricing-config";
import { IPaymentMethod } from "../../models/i-payment-method";
import { IBillingCustomer } from "../../models/i-billing-customer";
import { IPaymentStandingView } from "@zaeper/angular-dashboard-view-lib";
import { ISetupIntent } from "../../models/i-setup-intent";
import { IRegistryLicensePurchase } from "../../models/i-registry-license-purchase";
import { ISubscription } from "../../models/i-subscription";
import { IInvoice } from "../../models/i-invoice";
import { IPaymentIntent } from "../../models/i-payment-intent";
import { IBillingConfig } from "../../models/i-billing-config";
import { ICreateSubscriptionForm } from "./forms/i-create-subscription-form";
import { IUpdateSubscriptionItemsForm } from "./forms/i-update-subscription-items-form";
import { ICancelSubscriptionForm } from "./forms/i-cancel-subscription-form";
import { HostUtil } from "../../utils/host-util";
import { firstValueFrom, Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { ARestClient, IOkResponse } from "@zaeper/communication-lib";
import { Injectable } from "@angular/core";

@Injectable()
export class BillingRestClient extends ARestClient implements IBillingRestClient {
	constructor(private readonly _httpClient: HttpClient) {
		super();
	}

	getBillingConfig(): Promise<IBillingConfig> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/billing/config");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IBillingConfig>);
	}

	listPaymentMethods(companyId: string): Promise<IPaymentMethod[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/payment-methods`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IPaymentMethod[]>);
	}

	getBillingCustomer(companyId: string): Promise<IBillingCustomer> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/customer`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IBillingCustomer>);
	}

	getStanding(companyId: string): Promise<IPaymentStandingView> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/standing`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IPaymentStandingView>);
	}

	createPortalSession(companyId: string, language: string): Promise<{ url: string }> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/portal-session`);

		return firstValueFrom(this._httpClient.post(endpointUrl, { language }, { withCredentials: true }) as Observable<{ url: string }>);
	}

	updateBillingEmail(companyId: string, email: string): Promise<IBillingCustomer> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/email`);

		return firstValueFrom(this._httpClient.put(endpointUrl, { email }, { withCredentials: true }) as Observable<IBillingCustomer>);
	}

	createSetupIntent(companyId: string): Promise<ISetupIntent> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/payment-methods/setup`);

		return firstValueFrom(this._httpClient.post(endpointUrl, {}, { withCredentials: true }) as Observable<ISetupIntent>);
	}

	setDefaultPaymentMethod(companyId: string, stripePaymentMethodId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/payment-methods/${stripePaymentMethodId}/default`);

		return firstValueFrom(this._httpClient.put(endpointUrl, {}, { withCredentials: true }) as Observable<IOkResponse>);
	}

	detachPaymentMethod(companyId: string, stripePaymentMethodId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/payment-methods/${stripePaymentMethodId}`);

		return firstValueFrom(this._httpClient.delete(endpointUrl, { withCredentials: true }) as Observable<IOkResponse>);
	}

	getSubscriptions(companyId: string): Promise<ISubscription[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/subscription`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<ISubscription[]>);
	}

	getPrices(companyId: string): Promise<IPriceList> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/prices`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IPriceList>);
	}

	createSubscription(companyId: string, form: ICreateSubscriptionForm): Promise<ISubscription> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/subscription`);

		return firstValueFrom(this._httpClient.post(endpointUrl, form, { withCredentials: true }) as Observable<ISubscription>);
	}

	updateSubscriptionItems(companyId: string, form: IUpdateSubscriptionItemsForm): Promise<ISubscription> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/subscription/items`);

		return firstValueFrom(this._httpClient.put(endpointUrl, form, { withCredentials: true }) as Observable<ISubscription>);
	}

	cancelSubscription(companyId: string, form: ICancelSubscriptionForm): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/subscription/cancel`);

		return firstValueFrom(this._httpClient.post(endpointUrl, form, { withCredentials: true }) as Observable<IOkResponse>);
	}

	purchaseRegistryLicense(companyId: string): Promise<IRegistryLicensePurchase> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/registry-license/purchase`);

		return firstValueFrom(this._httpClient.post(endpointUrl, {}, { withCredentials: true }) as Observable<IRegistryLicensePurchase>);
	}

	cancelRegistryLicense(companyId: string): Promise<void> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/registry-license/cancel`);

		return firstValueFrom(this._httpClient.post<void>(endpointUrl, {}, { withCredentials: true }));
	}

	listInvoices(companyId: string): Promise<IInvoice[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/invoices`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IInvoice[]>);
	}

	getUpcomingInvoice(companyId: string): Promise<IInvoice> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/invoices/upcoming`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IInvoice>);
	}

	chargeAdditionalCompany(companyId: string): Promise<IPaymentIntent> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/company/${companyId}/billing/charge/additional-company`);

		return firstValueFrom(this._httpClient.post(endpointUrl, {}, { withCredentials: true }) as Observable<IPaymentIntent>);
	}
}
